<script setup>
import { computed } from 'vue'
import { Head, useForm, usePage } from '@inertiajs/vue3'
import InvestLayout from '@/Layouts/InvestLayout.vue'
import BrandHero from '@/Components/Public/BrandHero.vue'
import LuxButton from '@/Components/Public/LuxButton.vue'
import { platformLabel } from '@/lib/platformLabel'

defineOptions({ layout: InvestLayout })

const props = defineProps({
  content:      { type: Object, required: true },
  sent:         { type: Boolean, default: false },
  chat_intents: { type: Object, default: () => ({}) },
})
const c = props.content
const page = usePage()
const base = computed(() => page.props.base || '')


const form = useForm({
  name: '', contact: '', interest: 'both', budget_range: '', message: '', website: '',
})
// A plain `ref(!props.sent)` would only be evaluated once at mount and go
// stale: Inertia's useForm().post() defaults to preserveState: true, so the
// redirect back to this same page after a successful submit patches props
// on the SAME component instance (no remount) — a snapshot ref would never
// see sent flip to true. A computed tracks the reactive prop correctly.
const showForm = computed(() => !props.sent)

function submit() {
  form.transform(d => ({
    investment_inquiry: {
      name: d.name, contact: d.contact, interest: d.interest,
      budget_range: d.budget_range, message: d.message,
    },
    website: d.website,
  })).post(`${base.value}/inquiries`, { preserveScroll: true })
}
</script>

<template>
  <Head :title="c.title" />

  <BrandHero kind="invest" :eyebrow="c.hero.eyebrow" :title="c.hero.headline">
    <p class="inv-hero__sub">{{ c.hero.sub }}</p>
  </BrandHero>

  <section class="inv-body">
    <div class="inv-block">
      <h2 class="inv-heading">{{ c.cycle.heading }}</h2>
      <ol class="inv-steps">
        <li v-for="(s, i) in c.cycle.steps" :key="i" class="inv-step">
          <span class="inv-step__n fw-value">{{ i + 1 }}</span>
          <div>
            <h3 class="inv-step__title">{{ s.title }}</h3>
            <p class="inv-prose">{{ s.body }}</p>
          </div>
        </li>
      </ol>
    </div>

    <div class="inv-block">
      <h2 class="inv-heading">{{ c.tracks.heading }}</h2>
      <div class="inv-tracks">
        <div v-for="track in [c.tracks.vehicles, c.tracks.activities, c.tracks.property, c.tracks.yachts, c.tracks.food]" :key="track.title" class="inv-card">
          <h3 class="inv-card__title">{{ track.title }}</h3>
          <p class="inv-prose">{{ track.body }}</p>
          <ul class="inv-points">
            <li v-for="p in track.points" :key="p">{{ p }}</li>
          </ul>
        </div>
        <a v-if="c.tracks.idea" href="#inquire" class="inv-card inv-card--idea">
          <h3 class="inv-card__title">{{ c.tracks.idea.title }}</h3>
          <p class="inv-prose">{{ c.tracks.idea.body }}</p>
          <span class="inv-card__cta">{{ c.tracks.idea.cta }} →</span>
        </a>
      </div>
    </div>

    <div class="inv-card inv-card--example">
      <h2 class="inv-heading">{{ c.example.heading }}</h2>
      <p class="inv-prose">{{ c.example.body }}</p>
      <p class="inv-disclaimer">{{ c.example.disclaimer }}</p>
    </div>

    <div class="inv-block">
      <h2 class="inv-heading">{{ c.why.heading }}</h2>
      <div class="inv-why">
        <div v-for="(w, i) in c.why.items" :key="i" class="inv-why__item">
          <h3 class="inv-why__title">{{ w.title }}</h3>
          <p class="inv-prose">{{ w.body }}</p>
        </div>
      </div>
    </div>

    <div class="inv-card">
      <h2 class="inv-heading">{{ c.faq.heading }}</h2>
      <details v-for="(f, i) in c.faq.items" :key="i" class="inv-faq">
        <summary class="inv-faq__q">{{ f.q }}</summary>
        <p class="inv-faq__a inv-prose">{{ f.a }}</p>
      </details>
      <p class="inv-terms-note">{{ c.terms_note }}</p>
    </div>

    <div id="inquire" class="inv-card inv-card--form">
      <template v-if="sent && !showForm">
        <h2 class="inv-heading">{{ c.form.success.title }}</h2>
        <p class="inv-prose">{{ c.form.success.body }}</p>
      </template>
      <template v-else>
        <h2 class="inv-heading">{{ c.form.heading }}</h2>
        <p class="inv-prose">{{ c.form.sub }}</p>
        <form class="inv-form" @submit.prevent="submit">
          <label class="inv-field">
            <span>{{ c.form.name }}</span>
            <input v-model="form.name" type="text" required maxlength="80">
          </label>
          <label class="inv-field">
            <span>{{ c.form.contact }}</span>
            <input v-model="form.contact" type="text" required maxlength="160">
          </label>
          <div class="inv-form__row">
            <label class="inv-field">
              <span>{{ c.form.interest }}</span>
              <select v-model="form.interest">
                <option v-for="(label, key) in c.form.interest_options" :key="key" :value="key">{{ label }}</option>
              </select>
            </label>
            <label class="inv-field">
              <span>{{ c.form.budget }}</span>
              <select v-model="form.budget_range">
                <option value="">—</option>
                <option v-for="(label, key) in c.form.budget_options" :key="key" :value="key">{{ label }}</option>
              </select>
            </label>
          </div>
          <label class="inv-field">
            <span>{{ c.form.message }}</span>
            <textarea v-model="form.message" rows="3" maxlength="2000"></textarea>
          </label>
          <input v-model="form.website" type="text" name="website" tabindex="-1" autocomplete="off" class="inv-honeypot" aria-hidden="true">
          <LuxButton type="submit" variant="solid" tone="light" glyph="arrow" :disabled="form.processing">{{ c.form.submit }}</LuxButton>
        </form>
      </template>
    </div>

    <div v-if="Object.keys(chat_intents).length" class="inv-block inv-chat">
      <h2 class="inv-heading">{{ c.chat.heading }}</h2>
      <p class="inv-prose">{{ c.chat.sub }}</p>
      <div class="inv-chat__btns">
        <LuxButton v-for="(url, platform, i) in chat_intents" :key="platform" :href="url" :variant="i === 0 ? 'solid' : 'line'" tone="light" :glyph="platform" target="_blank" rel="noopener">
          {{ platformLabel(platform) }}
        </LuxButton>
      </div>
    </div>

    <div class="inv-card inv-card--trust">
      <h2 class="inv-heading">{{ c.trust.heading }}</h2>
      <p class="inv-prose">{{ c.trust.body }}</p>
    </div>
  </section>
</template>

<style scoped>
/* Invest landing — same voice as the home: serif headings with a gold rule,
   gold numerals, hairline cards, the luxury button. */
.inv-hero__sub { margin: 0.9rem 0 0; font-size: clamp(1.05rem, 2vw, 1.25rem); line-height: 1.5; max-width: 54ch; opacity: 0.94; }
.inv-body { max-width: 64rem; margin: 0 auto; padding: clamp(2.5rem, 5vw, 4rem) clamp(1.25rem, 4vw, 2rem) 4rem; display: grid; gap: clamp(2.5rem, 5vw, 3.5rem); }
.inv-heading { margin: 0 0 1rem; font-family: var(--display); font-weight: 500; font-size: clamp(1.7rem, 2.8vw, 2.3rem); line-height: 1.08; letter-spacing: -0.005em; color: var(--ink); text-wrap: balance; }
.inv-heading::before { content: ""; display: block; width: 2rem; height: 1px; background: var(--gold); margin-bottom: 0.85rem; }
.inv-prose { margin: 0 0 0.75rem; color: var(--inkSoft); line-height: 1.6; }
.inv-steps { list-style: none; margin: 0; padding: 0; }
.inv-step { display: grid; grid-template-columns: 3rem 1fr; gap: 1rem; padding: 1rem 0; border-top: 1px solid color-mix(in oklch, var(--gold) 35%, var(--line)); }
.inv-step:last-child { border-bottom: 1px solid color-mix(in oklch, var(--gold) 35%, var(--line)); }
.inv-step__n { font-family: var(--display); font-size: 1.5rem; line-height: 1; color: var(--gold); padding-top: 0.15rem; }
.inv-step__title { margin: 0 0 0.25rem; font-family: var(--display); font-weight: 500; font-size: 1.45rem; line-height: 1.1; color: var(--ink); }
.inv-card { background: var(--surface); border: 1px solid color-mix(in oklch, var(--gold) 40%, var(--line)); border-radius: 1.25rem; padding: 1.5rem; }
.inv-tracks { display: grid; gap: 1rem; grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr)); }
.inv-card__title { margin: 0 0 0.5rem; font-family: var(--display); font-weight: 500; font-size: 1.5rem; line-height: 1.1; color: var(--ink); }
.inv-points { margin: 0; padding-left: 1.1rem; color: var(--inkSoft); line-height: 1.7; }
.inv-disclaimer { margin: 0.5rem 0 0; font-size: 0.85rem; color: var(--muted); font-style: italic; }
.inv-why { display: grid; gap: 1.25rem 2rem; grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr)); }
.inv-why__item::before { content: ""; display: block; width: 1.6rem; height: 1px; background: var(--gold); margin-bottom: 0.7rem; }
.inv-why__title { margin: 0 0 0.25rem; font-family: var(--display); font-weight: 500; font-size: 1.3rem; line-height: 1.15; color: var(--ink); }
.inv-faq { border-top: 1px solid var(--line); padding: 0.75rem 0; }
.inv-faq:first-of-type { border-top: none; }
.inv-faq__q { cursor: pointer; font-weight: 600; color: var(--ink); }
.inv-faq__a { margin-top: 0.5rem; }
.inv-terms-note { margin: 1rem 0 0; font-size: 0.9rem; color: var(--muted); font-style: italic; }
.inv-form { display: grid; gap: 1rem; margin-top: 1rem; }
.inv-form__row { display: grid; gap: 1rem; grid-template-columns: repeat(auto-fit, minmax(14rem, 1fr)); }
.inv-field { display: grid; gap: 0.4rem; }
.inv-field > span { font-size: 0.68rem; letter-spacing: var(--eyebrow-tracking, 0.18em); text-transform: uppercase; font-weight: 600; color: var(--inkSoft); }
.inv-field input, .inv-field select, .inv-field textarea {
  padding: 0.75rem 0.9rem; border: 1px solid var(--line); border-radius: 0.75rem;
  background: var(--bg); color: var(--ink); font: inherit;
}
.inv-field input:focus, .inv-field select:focus, .inv-field textarea:focus { outline: none; border-color: var(--gold); box-shadow: 0 0 0 3px color-mix(in oklch, var(--gold) 20%, transparent); }
.inv-honeypot { position: absolute; left: -9999px; }
.inv-chat__btns { display: flex; flex-wrap: wrap; gap: 0.75rem; }
.inv-card--trust { background: linear-gradient(170deg, var(--surface) 0%, var(--sky, var(--surfaceAlt)) 100%); }
/* "Have an idea?" — the open door at the end of the tracks. */
.inv-card--idea { display: grid; align-content: start; gap: 0.25rem; text-decoration: none; color: inherit; border-style: dashed; border-color: color-mix(in oklch, var(--gold) 70%, var(--line)); background: linear-gradient(170deg, var(--surface) 0%, var(--goldSoft, var(--surfaceAlt)) 100%); transition: transform 150ms ease, border-color 150ms ease; }
.inv-card--idea:hover { transform: translateY(-2px); border-color: var(--gold); }
.inv-card__cta { margin-top: 0.5rem; font-size: 0.74rem; letter-spacing: 0.14em; text-transform: uppercase; font-weight: 600; color: color-mix(in oklch, var(--gold) 62%, var(--ink)); }
</style>
